import {
  Box, Button, Heading, HStack, Icon,
  IconButton, Link,
  Menu, MenuButton,
  MenuDivider, MenuItem,
  MenuList, Popover, PopoverContent, PopoverTrigger, Portal as ChakraPortal, Skeleton, SkeletonText, Stack, Tab, Table, TableContainer, TabList, TabPanel, TabPanels, Tabs, Tbody, Td, Text as ChakraText, Th, Thead, Tooltip, Tr, useDisclosure, useModalContext, VStack
} from '@chakra-ui/react'
import moment from 'moment'
import React, {
  useCallback,
  useContext,
  useEffect, useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { CiDesktop, CiMobile1 } from 'react-icons/ci'
import { FiEdit, FiTrendingUp } from 'react-icons/fi'
import { RiDeleteBin2Fill } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import { PermissionsEnum } from '../../../utils'
import { FeaturesEnum } from '../../../utils/features'
import CustomPlanCTA from '../../components/CustomPlan'
import FreePlanCTA from '../../components/FreePlan'
import AlertPreferencesModal from '../../components/Performance/AlertPreferencesModal'
import DeleteUrlModal from '../../components/Performance/DeleteUrlModal'
import HistoryModal from '../../components/Performance/HistoryModal'
import TrackUrlModal from '../../components/Performance/TrackUrlModal'
import PermissionGuard from '../../components/PermissionGuard'
import type { PerformanceItemsResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'


function Portal({ children }) {
  try {
    const modalContext = useModalContext();
    return <ChakraPortal containerRef={modalContext?.dialogRef}>{children}</ChakraPortal>;
  } catch {
    return <ChakraPortal>{children}</ChakraPortal>;
  }
}

const PASS_SCORE_THRESHOLD = 0.9
const FAIL_SCORE_THRESHOLD = 0.5

const PerformanceHomeLayout = (): React.ReactElement => {
  const { t } = useTranslation()

  const { user } = useContext(UserContext)

  const [performanceItemsRefresh, setPerformanceItemsRefresh] =
    useState<boolean>(false)

  const {
    data: performanceItems,
    get: getPerformanceItemsFetch,
    loading: loadingPerformanceItems,
  } = useFetch<PerformanceItemsResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  useEffect(() => {
    const getPerformanceItems = async (): Promise<void> => {
      if (
        user?.clientNumber &&
        user.pricingPlan.featuresList.includes(FeaturesEnum.PERFORMANCE_CENTER)
      ) {
        await getPerformanceItemsFetch(
          `/api/performance/${user.clientNumber}/url`,
        )
      }
    }
    void getPerformanceItems()
  }, [user, performanceItemsRefresh])

  const refreshPerformanceItems = useCallback(() => {
    setPerformanceItemsRefresh(!performanceItemsRefresh)
  }, [performanceItemsRefresh])


  const { request } = useFetch<PerformanceItemsResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [editingUrl, setEditingUrl] = useState<string | null>(null)
  const [editingUrlId, setEditingUrlId] = useState<string | null>(null)

  const {
    isOpen: isOpenTrackUrlModal,
    onOpen: onOpenTrackUrlModal,
    onClose: onCloseTrackUrlModal,
  } = useDisclosure()

  const {
    isOpen: isOpenAlertModal,
    onOpen: onOpenAlertModal,
    onClose: onCloseAlertModal,
  } = useDisclosure()


  return (
    <React.Fragment>
      {user?.pricingPlan.featuresList.includes(
        FeaturesEnum.PERFORMANCE_CENTER,
      ) ? (
        <MainContentHeadingBox>
          <Heading
            as="h5"
            size="md"
            fontWeight="semibold"
            color="appDarkIndigo"
          >
            {t('performance_center', 'Performance Center')}
          </Heading>
          <PermissionGuard permission={PermissionsEnum.SELF_PERFORMANCE_MANAGE}>
            {(hasPermission: boolean) =>
              hasPermission ? (
                <HStack spacing={4}>
                  <Button
                    onClick={() => {
                      setEditingUrl(null)
                      onOpenTrackUrlModal()
                    }}
                  >
                    {t('track_new_url', 'Track New URL')}
                  </Button>
                  <Button variant="outline" onClick={onOpenAlertModal}>
                    {t('alert_preference', 'Alert Preference')}
                  </Button>
                </HStack>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
        </MainContentHeadingBox>
      ) : (
        <MainContentHeadingBox>
          <Stack direction={['column']} spacing={1}>
            <Heading as="h2" size="md" fontWeight="600" color="appDarkIndigo">
              {t('performance_center', 'Performance Center')}
            </Heading>
            <ChakraText color="appBattleshipGrey">
              {t(
                'performance_center_description',
                'Track image optimization and fix issues for key URLs by adding them here',
              )}
            </ChakraText>
          </Stack>
        </MainContentHeadingBox>
      )}

      <MainContentWrapper>
        {user?.pricingPlan.featuresList.includes(
          FeaturesEnum.PERFORMANCE_CENTER,
        ) ? (
          <>
            {loadingPerformanceItems &&
              <SummaryTableSkeleton />
            }
            {!loadingPerformanceItems &&
              <SummaryTable performanceItems={performanceItems} refreshPerformanceItems={refreshPerformanceItems} />
            }
          </>
        ) : user?.pricingPlan.isPaidPlan ? (
          <CustomPlanCTA feature={FeaturesEnum.PERFORMANCE_CENTER} />
        ) : (
          <FreePlanCTA />
        )}
      </MainContentWrapper>

      <TrackUrlModal
        isOpen={isOpenTrackUrlModal}
        onClose={onCloseTrackUrlModal}
        url={editingUrl}
        urlId={editingUrlId}
        user={user}
        label={""}
        request={request}
        requestMethod={"post"}
        refreshPerformanceItems={refreshPerformanceItems}
      />
      <AlertPreferencesModal
        isOpen={isOpenAlertModal}
        onClose={onCloseAlertModal}
        user={user}
        request={request}
      />
    </React.Fragment>
  )
}

const SummaryTable = ({ performanceItems, refreshPerformanceItems }): React.ReactElement => {
  if (!performanceItems || performanceItems.length === 0) return (
    <Box>
      Currently no URLs are being tracked. Add a URL to start tracking.
    </Box>
  );

  return (
    <Tabs>
      <TabList>
        <Tab gap={4}>
          <CiMobile1 />
          Mobile
        </Tab>
        <Tab gap={4}>
          <CiDesktop />
          Desktop
        </Tab>
      </TabList>

      <TabPanels>
        <TabPanel>
          <HistoryTable deviceType="mobile" performanceItems={performanceItems} refreshPerformanceItems={refreshPerformanceItems} />
        </TabPanel>
        <TabPanel>
          <HistoryTable deviceType="desktop" performanceItems={performanceItems} refreshPerformanceItems={refreshPerformanceItems} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  )
}

const HistoryTable = ({ deviceType, performanceItems, refreshPerformanceItems }) => {
  if (!deviceType) return null;

  if (!performanceItems.length) return null;

  return (
    <TableContainer whiteSpace={"pre-wrap"}>
      <Table>
        <Thead background={"white"}>
          <Tr borderBottom={"1px solid #e6e7ea"}>
            <Th></Th>
            <Th>Latest performance score</Th>
            <Th>Image Optimization</Th>
            <Th>Image Responsiveness</Th>
            <Th>Image Format Modernization</Th>
            <Th>Video Format Modernization</Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {performanceItems.map((item, index) => (
            <HistoryTableRow key={index} deviceType={deviceType} item={item} refreshPerformanceItems={refreshPerformanceItems} />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  )
}

const HistoryTableRow = ({ deviceType, item, refreshPerformanceItems }) => {
  if (!deviceType) {
    return null;
  }

  let last90Days = [] as string[];
  for (let i = 89; i >= 0; i--) {
    last90Days.push(moment().subtract(i, 'days').format('YYYY-MM-DD'));
  }

  const _historicalData = item?.historicalData || [];

  const historicalData = last90Days.map(date => {
    const match = _historicalData?.find(item => item.date === date);
    return match ? match : {
      date
    };
  });

  const latestScore = (_historicalData[0]?.performanceScore?.[deviceType]) || null;
  const displayScore = latestScore ? Math.round(latestScore * 100) : "-";
  let latestScoreDate;
  if (latestScore) latestScoreDate = `As on ${moment(_historicalData[0]?.date).format("Do MMM, YY")}`;

  const { user } = useContext(UserContext)

  const { request } = useFetch<PerformanceItemsResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const auditTypes = [
    "uses-optimized-images",
    "uses-responsive-images",
    "modern-image-formats",
    "modern-video-formats"
  ];

  const {
    isOpen: isOpenTrackUrlModal,
    onOpen: onOpenTrackUrlModal,
    onClose: onCloseTrackUrlModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteModal,
    onOpen: onOpenDeleteModal,
    onClose: onCloseDeleteModal,
  } = useDisclosure()

  const {
    isOpen: isOpenHistoryModal,
    onOpen: onOpenHistoryModal,
    onClose: onCloseHistoryModal,
  } = useDisclosure()

  return (
    <>
      <Tr borderBottom={"solid 1px #e6e7ea"} cursor={"pointer"} _hover={{ backgroundColor: "appPaleGreyTwo" }}>
        <Td border={"none"}>
          <Tooltip label={item.websiteUrl}>
            {item.label}
          </Tooltip>
        </Td>
        <Td border={"none"}>
          <Tooltip label={latestScoreDate ? latestScoreDate : "Performance score of latest analysis"}>
            <ChakraText fontWeight={"semibold"} fontSize="20px">
              {displayScore}
            </ChakraText>
          </Tooltip>
        </Td>
        {auditTypes.map((auditType, index) => (
          <Td key={index} border={"none"}>
            <HStack spacing={"2px"}>
              {historicalData.slice(-7).map((data, index) => (
                <Bar
                  key={index}
                  deviceType={deviceType}
                  score={data?.audits?.[deviceType]?.[auditType]}
                  date={data?.date}
                  taskId={data?.taskId}
                />
              ))}
            </HStack>
          </Td>
        ))}
        <Td border={"none"}>
          <Button size={"sm"}
            leftIcon={<FiTrendingUp />}
            variant={"outline"} onClick={() => {
              onOpenHistoryModal();
            }}>
            View trends
          </Button>
        </Td>
        <Td border={"none"}>
          <Menu>
            <MenuButton as={IconButton} variant="link" icon={<BsThreeDotsVertical />} />
            <MenuList>
              <MenuItem onClick={() => {
                onOpenTrackUrlModal();
              }}>
                <HStack>
                  <Icon as={FiEdit} />
                  <ChakraText>Edit</ChakraText>
                </HStack>
              </MenuItem>
              <MenuDivider />
              <MenuItem onClick={() => {
                onOpenDeleteModal();
              }}>
                <HStack>
                  <Icon as={RiDeleteBin2Fill} color="appBrick" />
                  <ChakraText color="appBrick">Delete</ChakraText>
                </HStack>
              </MenuItem>
            </MenuList>
          </Menu>
        </Td>
      </Tr>
      <TrackUrlModal
        isOpen={isOpenTrackUrlModal}
        onClose={onCloseTrackUrlModal}
        label={item.label}
        url={item.websiteUrl}
        urlId={item.subscribedUrlId}
        user={user}
        request={request}
        requestMethod={"put"}
        refreshPerformanceItems={refreshPerformanceItems}
      />
      <DeleteUrlModal
        url={item.websiteUrl}
        urlId={item.subscribedUrlId}
        onClose={onCloseDeleteModal}
        isOpen={isOpenDeleteModal}
        request={request}
        refreshPerformanceItems={refreshPerformanceItems}
        clientNumber={user?.clientNumber}
      />
      <HistoryModal
        isOpen={isOpenHistoryModal}
        onClose={onCloseHistoryModal}
        url={item.websiteUrl}
        label={item.label}
        latestRerport={item?.historicalData?.[0]}
        scoreColor={scoreColor}
        AuditIcon={AuditIcon}
        Bar={Bar}
        historicalData={historicalData}
        deviceType={deviceType}
      />
    </>
  )
}

const Bar = ({ score, date, taskId, deviceType }) => {
  return (
    <Popover trigger={"hover"}>
      <PopoverTrigger>
        <Box cursor={"pointer"} bg={`${scoreColor(score)}`} width={"12px"} height={"16px"} borderRadius={"sm"}></Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="fit-content">
          <Box p={4}>
            <BarPopoverContent deviceType={deviceType} score={score} date={date} taskId={taskId} />
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

const BarPopoverContent = ({ score, date, taskId, deviceType }) => {
  if (score == null || !taskId || !date) {
    return (
      <VStack align={"left"} spacing={1}>
        <Box fontWeight={"semibold"}>No report available</Box>
        {date &&
          <ChakraText fontSize={"14px"} color={"appBlueyGrey"}>{moment(date).format("Do MMM, YY")}</ChakraText>
        }
      </VStack>
    )
  }
  return (
    <VStack spacing={1} align={"left"}>
      <Box>
        <HStack>
          <AuditIcon score={score} />
          {score < PASS_SCORE_THRESHOLD &&
            <ChakraText fontWeight={"semibold"}>Needs improvement</ChakraText>
          }
          {score >= PASS_SCORE_THRESHOLD &&
            <ChakraText fontWeight={"semibold"}>Looks good</ChakraText>
          }
        </HStack>

      </Box>
      <HStack divider={<>&nbsp;•&nbsp;</>}>
        {date &&
          <ChakraText fontSize={"14px"} color={"appBlueyGrey"}>{moment(date).format("Do MMM, YY")}</ChakraText>
        }
        {taskId &&
          <Link href={`/website-analyzer?taskId=${taskId}&device=${deviceType}&hht=true`} isExternal={true}>View report</Link>
        }
      </HStack>
    </VStack>
  )
}

const AuditIcon = ({ score = null }) => {
  if (score === null) {
    return <Box height='10px' width='10px' bg={scoreColor(score)}></Box>
  }

  if (score < FAIL_SCORE_THRESHOLD) {
    return <Box height='10px' width='10px'><Box height='0' width='0' borderLeft="5px solid transparent"
      borderRight="5px solid transparent"
      borderBottom={`10px solid red`}></Box></Box>
  } else if (score < PASS_SCORE_THRESHOLD) {
    return <Box height='10px' width='10px' bg={`${scoreColor(score)}`}></Box>
  } else {
    return <Box height='10px' width='10px' bg={scoreColor(score)} borderRadius={"full"}></Box>
  }
}


const scoreColor = (score = null) => {
  if (score === null) return "gray.300";
  if (score < FAIL_SCORE_THRESHOLD) {
    return "red.500"
  } else if (score < PASS_SCORE_THRESHOLD) {
    return "orange.500"
  } else {
    return "green.500"
  }
}

export default PerformanceHomeLayout


const SummaryTableSkeleton = (): React.ReactElement => {
  return (
    <Tabs>
      <TabList>
        <Tab gap={2}>
          <CiMobile1 />
          <Skeleton height="20px" width="50px" />
        </Tab>
        <Tab gap={2}>
          <CiDesktop />
          <Skeleton height="20px" width="50px" />
        </Tab>
      </TabList>

      <TabPanels>
        <TabPanel>
          <HistoryTableSkeleton />
        </TabPanel>
        <TabPanel>
          <HistoryTableSkeleton />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

const HistoryTableSkeleton = () => {
  return (
    <Box overflowX="auto">
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>
              <Skeleton height="20px" width="50px" />
            </Th>
            <Th>
              <Skeleton height="20px" width="100px" />
            </Th>
            <Th>
              <Skeleton height="20px" width="100px" />
            </Th>
            <Th>
              <Skeleton height="20px" width="100px" />
            </Th>
            <Th>
              <Skeleton height="20px" width="100px" />
            </Th>
            <Th>
              <Skeleton height="20px" width="50px" />
            </Th>
            {/* Add more headers as needed */}
          </Tr>
        </Thead>
        <Tbody>
          {[...Array(5)].map((_, index) => (
            <Tr key={index}>
              <Td>
                <SkeletonText noOfLines={1} spacing="4" />
              </Td>
              <Td>
                <SkeletonText noOfLines={1} spacing="4" />
              </Td>
              <Td>
                <SkeletonText noOfLines={1} spacing="4" />
              </Td>
              <Td>
                <SkeletonText noOfLines={1} spacing="4" />
              </Td>
              <Td>
                <SkeletonText noOfLines={1} spacing="4" />
              </Td>
              <Td>
                <SkeletonText noOfLines={1} spacing="4" />
              </Td>
              {/* Add more columns as needed */}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};